import * as React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';

/**
 * Signed period-over-period cell for the compact analytics tables.
 *
 * Extracted from the Hotspot Analysis page so the Emissions Breakdown page shows deltas identically
 * rather than growing a second near-copy that drifts. The convention it encodes is the repo rule:
 * colour the direction ICON (green for a decrease, red for an increase, grey for no change) and
 * leave the number itself alone.
 */

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value) ? '–' : `${value.toFixed(1)}%`;

export const DeltaCell: React.FC<{
  deltaAbs: number | null;
  deltaPct: number | null;
  isNew: boolean;
}> = ({ deltaAbs, deltaPct, isNew }) => {
  if (isNew) return <Chip size='small' label='New' color='warning' variant='outlined' />;
  if (deltaAbs === null) return <Typography variant='body2' color='text.secondary'>–</Typography>;
  const isDecrease = deltaAbs < 0;
  const isNoChange = Math.abs(deltaAbs) < 0.005;
  const color = isNoChange ? 'text.secondary' : isDecrease ? 'success.main' : 'error.main';
  const Icon = isNoChange ? RemoveIcon : isDecrease ? ArrowDownwardIcon : ArrowUpwardIcon;
  return (
    <Stack direction='row' alignItems='center' spacing={0.25} justifyContent='flex-end'>
      <Icon sx={{ fontSize: '0.85rem', color }} />
      <Typography variant='body2' sx={{ color, fontVariantNumeric: 'tabular-nums' }}>
        {fmtTco2e.format(Math.abs(deltaAbs))}{deltaPct !== null ? ` (${fmtPct(Math.abs(deltaPct))})` : ''}
      </Typography>
    </Stack>
  );
};
